import React, { PropsWithChildren, useEffect, useRef, useState } from 'react';
import { Divider, Table, Popconfirm, Card, Tooltip, Switch, Input, Button, Tag, Row, Col, Drawer, InputNumber, DatePicker, Form, Select } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, RightSquareOutlined, EyeOutlined, EditOutlined, SearchOutlined } from '@ant-design/icons';
import { ColumnProps } from 'antd/lib/table';
import { HrmsEmployeeDetailsDto, RolesDto } from '@gtpl/shared-models/masters';
import { HrmsEmpDetService, RolesService } from '@gtpl/shared-services/masters';
import Highlighter from 'react-highlight-words';
import { HrmsEmployeeDetailsForm } from '@gtpl/pages/master/master-components/hrms-employee-details-form';
import { EmployeeRolesEnum } from '@gtpl/shared-models/common-models';
import { EmployeeLeavesService, TypesOfLeavesService } from '@gtpl/shared-services/hrms';


import { Link } from 'react-router-dom';
import { AlertMessages } from '@gtpl/shared-utils/alert-messages';

import './employee-leaves-grid.css';
import { EmpLeaveDTO, EmployeeLeavesCreateDTO, GetEmpLeaves, TypesOfLeavesDto } from '@gtpl/shared-models/hrms';
import UndoOutlined from '@ant-design/icons/lib/icons/UndoOutlined';

/* eslint-disable-next-line */
export interface EmployeeLeavesGridProps { }

export function EmployeeLeavesGrid(
  props: EmployeeLeavesGridProps
) {


  const getColumnSearchProps = (dataIndex: string) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 8 }}>
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ width: 188, marginBottom: 8, display: 'block' }}
        />
        <Button
          type="primary"
          onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
          icon={<SearchOutlined />}
          size="small"
          style={{ width: 90, marginRight: 8 }}
        >
          Search
        </Button>
        <Button onClick={() => handleReset(clearFilters)} size="small" style={{ width: 90 }}>
          Reset
        </Button>
      </div>
    ),
    filterIcon: filtered => (
      <SearchOutlined type="search" style={{ color: filtered ? '#1890ff' : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        ? record[dataIndex]
          .toString()
          .toLowerCase()
          .includes(value.toLowerCase())
        : false,
    onFilterDropdownVisibleChange: visible => {
      if (visible) { setTimeout(() => searchInput.current.select()); }
    },
    render: text =>
      text ? (
        searchedColumn === dataIndex ? (
          <Highlighter
            highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
            searchWords={[searchText]}
            autoEscape
            textToHighlight={text.toString()}
          />
        ) : text
      )
        : null

  });

  const commonColumns: any[] = [
    {
      title: 'S No',
      key: 'sno',
      width: '70px',
      //  responsive: ['sm'],
      render: (text, object, index) => (page - 1) * 10 + (index + 1)
    },

    {
      title: 'Employee Code',
      dataIndex: 'employeeCode',
      // responsive: ['sm'],
      sorter: (a, b) => a.employeeCode - b.employeeCode,
      width: '250px',
      sortDirections: ['descend', 'ascend'],
      ...getColumnSearchProps('employeeCode')
    },
    {
      title: 'Employee Name',
      dataIndex: 'employeeName',
      width: '350px',
      // responsive: ['lg'],
      sorter: (a, b) => a.employeeName?.localeCompare(b.employeeName),
      sortDirections: ['descend', 'ascend'],
      ...getColumnSearchProps('employeeName')
    },
    {
      title: 'Designation',
      dataIndex: 'designation',
      // responsive: ['sm'],
      sorter: (a, b) => a.designation?.localeCompare(b.designation),
      sortDirections: ['descend', 'ascend'],
      ...getColumnSearchProps('designation')

    }
  ]
  const [columns, setColumns] = useState(commonColumns);
  const [searchText, setSearchText] = useState('');
  const [searchedColumn, setSearchedColumn] = useState('');
  const searchInput = useRef(null);
  const [page, setPage] = React.useState(1);
  const [employeeData, setEmployeeData] = useState<EmpLeaveDTO[]>([]);
  const [keyUpdate, setKeyUpdate] = useState<number>(0);
  const [visible, setVisible] = useState<boolean>(false);
  const [filtersData, setFiltersData] = useState<EmpLeaveDTO[]>([]);
  const [selectedRowKeysData, setSelectedRowKeysData] = useState<string[]>([]);
  const [selectedRows, setSelectedRows] = useState<EmpLeaveDTO[]>([]);
  
  const { Option } = Select;
  const [form] = Form.useForm();
  const employeeLeavesService = new EmployeeLeavesService();
  const typesOFLeavesService = new TypesOfLeavesService();

  useEffect(() => {
    getFiltersDropdownData(filtersData);
  }, [filtersData])

  useEffect(() => {
    getAllActiveEmployeesForLeaves();
    getTypesOfLeaves();
  }, []);

  const addLeavesToEmp = () => {
    const createdUser = localStorage.getItem('username') ? localStorage.getItem('username') : '';
    const reqObj = new EmployeeLeavesCreateDTO(createdUser, new Date(), selectedRows);
    employeeLeavesService.createEmployeeLeaves(reqObj).then((res) => {
      if (res.status) {
        AlertMessages.getSuccessMessage(res.internalMessage);
        setVisible(false);
        getAllActiveEmployeesForLeaves();
        getTypesOfLeaves();
      }
      else {
        AlertMessages.getErrorMessage(res.internalMessage);
      }
    })
  }
  const searchData = () => {
    const employeeName = form.getFieldValue('employeeName');
    const employeeCode = form.getFieldValue('employeeCode');
    const designation = form.getFieldValue('designation');

    let selectedData = filtersData;
    if (employeeName) {
      selectedData = selectedData.filter(item => item.employeeName === employeeName)
    }
    if (employeeCode) {
      selectedData = selectedData.filter(item => item.employeeCode === employeeCode)
    }
    if (designation) {
      selectedData = selectedData.filter(item => item.designation === designation)
    }
    setEmployeeData(selectedData);
  }

  const getFiltersDropdownData = (filtersData) => {
    let etdData: any[] = [];
    let employeesData: any[] = [];
    filtersData.forEach(element => {
      if (element.employeeName) {
        employeesData.push(element.employeeName)
      }
      if (element.employeeCode) {
        employeesData.push(element.employeeCode)
      }
      if (element.desisignation) {
        employeeData.push(element.desisignation)
      }
    });
    ///setEmployeeDetails(employeesData);
  }

  const rowSelection = {
    onChange: (selectedRowKeys, selectedRows) => {
      setSelectedRowKeysData(selectedRowKeys);
      setSelectedRows(selectedRows);
      setVisible(true);
    },
    getCheckboxProps: (record) => ({
      disabled: record.name === 'Disabled User',
      // Column configuration not to be checked
      name: record.name,
    }),
    selectedRowKeys: selectedRowKeysData
  };

  const getAllActiveEmployeesForLeaves = () => {
    employeeLeavesService.getAllEmployeeLeaves(new GetEmpLeaves(new Date().getFullYear().toString())).then(res => {
      if (res.status) {
        setEmployeeData(res.data);
        setFiltersData(res.data);
      } else {
        setEmployeeData([]);
        setFiltersData([]);
        AlertMessages.getErrorMessage(res.internalMessage);
      }
    }).catch(err => {
      setEmployeeData([]);
      setFiltersData([]);
      AlertMessages.getErrorMessage(err.message);
    })
  }
  const leavesChangeHandler = (employeData, leaveTypeId: number, leavesAlloted: number) => {
    const defaultVlaues = employeData.leaves.find(rec => rec.typeOfLeave === leaveTypeId);
    defaultVlaues.leavesAlloted = leavesAlloted;
    const newEmpDataState = employeeData.map(empData => {
      if (employeData.employeeId === empData.employeeId) {
        return employeData
      } else {
        empData
      }
    });
  }

  const getTypesOfLeaves = () => {
    const columns = [...commonColumns];
    typesOFLeavesService.getAllActiveTypesOfLeaves().then(res => {
      if (res.status) {
        res.data.forEach(element => {
          const dynCol = {
            title: element.typeOfLeave,
            dataIndex: element.typeOfLeave.replace(/ /g, ""),
            width: '120px',
            render: (text, record, index) => {
              const defaultVlaues = record.leaves.find(rec => rec.typeOfLeave === element.typeOfLeaveId)
              return <span>Available:{defaultVlaues.leavesAvailable}<InputNumber min={0} defaultValue={defaultVlaues.leavesAlloted} onChange={(value) => { leavesChangeHandler(record, defaultVlaues.typeOfLeave, value) }} /></span>
            }
          }
          columns.push(dynCol);
        });
        setColumns(columns);
        setKeyUpdate(keyUpdate + 1);
      }
    }
    )
  }

  function handleSearch(selectedKeys, confirm, dataIndex) {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  function handleReset(clearFilters) {
    clearFilters();
    setSearchText('');
  };
  const onReset = () => {
    // form.resetFields();
    form.resetFields();
    setSearchText('');
    setEmployeeData(filtersData);
  };
  const handleEmployeeName = (value) => {
    //setEmployeesData(value)
  }

  const handleEmployeeCode = (value) => {
    //setEmployeesData(value)
  }
  const handleDesignation = (value) => {
   // setEmployeesData(value)
  }

  return (
    <><Card title={<span style={{ color: 'white' }}>Employee Leaves</span>}
      style={{ textAlign: 'center' }} headStyle={{ backgroundColor: '#69c0ff', border: 0 }}>
      <br></br>
      <Form form={form} layout={'vertical'} style={{ padding: '0px' }}>
        <Form.Item style={{ display: "none" }} name="plantId" initialValue={Number(localStorage.getItem('unit_id'))}>
          <Input hidden />
        </Form.Item>
        <Row gutter={[24, 24]}>
          <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 5 }} lg={{ span: 5 }} xl={{ span: 5 }}>
            <Form.Item
              name="employeeName"
              label="Employee Name"
              rules={[
                {
                  required: false, message: 'Select Employee',
                },
              ]}
            >
              <Select
                placeholder="Select Employee"
                onChange={handleEmployeeName}
                allowClear
                style={{ width: '100%' }}
                showSearch
              >
                {employeeData.filter(emp=>emp.employeeName!='').map(dropData => {
                  return <Option value={dropData.employeeName}>{dropData.employeeName}</Option>;
                })}
              </Select>

            </Form.Item>
          </Col>
          <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 5 }} lg={{ span: 5 }} xl={{ span: 5 }}>
            <Form.Item
              name="employeeCode"
              label="Employee Code"
              rules={[
                {
                  required: false, message: 'Select Employee Code',
                },
              ]}
            >
              <Select
                placeholder="Select Employee Code"
                onChange={handleEmployeeCode}
                allowClear
                style={{ width: '100%' }}
                showSearch
              >
                {employeeData.filter(emp=>emp.employeeCode!='').map(dropData => {
                  return <Option value={dropData.employeeCode}>{dropData.employeeCode}</Option>;
                })}
              </Select>

            </Form.Item>
          </Col>

          <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 5 }} lg={{ span: 5 }} xl={{ span: 5 }}>
            <Form.Item
              name="designation"
              label="Designation"
              rules={[
                {
                  required: false, message: 'Select Designation',
                },
              ]}
            >
              <Select
                placeholder="Select Designation"
                onChange={handleDesignation}
                allowClear
                style={{ width: '100%' }}
                showSearch
              >
                {employeeData.filter(emp=>emp.designation!='').map(dropData => {
                  return <Option value={dropData.designation}>{dropData.designation}</Option>;
                })}
              </Select>

            </Form.Item>
          </Col>


          <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 5 }} lg={{ span: 5 }} xl={{ span: 5 }} style={{ marginTop: 30 }}>
            <Button
              type="primary"
              icon={<SearchOutlined />}
              // size="small"
              block onClick={() => searchData()}
              style={{ marginRight: 2, width: 100 }}
            >
              Search
            </Button>
            {/* <Button type='primary' block onClick={()=>searchData()}>Get</Button> */}
            <Button
              icon={<UndoOutlined />}
              onClick={() => onReset()}
            >
              Reset
            </Button>
          </Col>
        </Row>
      </Form>
      <Row>
        {visible && selectedRowKeysData.length ? <Button type='primary' style={{ alignContent: 'left' }} onClick={addLeavesToEmp}>Submit</Button> : ""}
      </Row>
      <br></br>

      <Table
        key={Date.now()}
        rowSelection={{
          ...rowSelection
        }}
        rowKey={record => record.employeeId + '-' + keyUpdate}
        columns={columns}
        dataSource={employeeData}
        bordered />

    </Card></>
  );
}

export default EmployeeLeavesGrid;
